import { useEffect, useRef } from "react";
import { SITE_MEDIA } from "@/constants/siteMedia";
import { openLiveDemoChat } from "@/constants/homeSections";

const cases = [
  {
    emoji: "🌙",
    tag: "Always available",
    title: "Day or night, Ava is here when you need someone to talk to.",
    description: "No appointments, no waiting rooms, no business hours. When the world feels quiet and you're left with your thoughts, Ava is just a message away—ready to listen at any hour.",
    image: SITE_MEDIA.usecase1,
    color: "#7C3AED",
    gradient: "from-[#f5f0ff] to-[#ede8ff]",
    iconBg: "#7C3AED",
    number: "01",
  },
  {
    emoji: "😮‍💨",
    tag: "No judgment",
    title: "Say what you want, how you want. Ava is here to listen—not judge.",
    description: "Express your raw thoughts without filtering yourself. Whether it's anger, sadness, confusion, or something you can't name yet, Ava meets you exactly where you are.",
    image: SITE_MEDIA.usecase2,
    color: "#A855F7",
    gradient: "from-[#faf0ff] to-[#f0e8ff]",
    iconBg: "#A855F7",
    number: "02",
  },
  {
    emoji: "💙",
    tag: "Private conversations",
    title: "Your thoughts stay yours. Your conversations are not shared or sold.",
    description: "What you share with Ava stays between you and Ava. No data mining, no targeted ads, no third-party access—just a safe space where your words belong only to you.",
    image: SITE_MEDIA.usecase3,
    color: "#6D28D9",
    gradient: "from-[#ede8ff] to-[#e8e0ff]",
    iconBg: "#6D28D9",
    number: "03",
  },
  {
    emoji: "✨",
    tag: "Helps you think clearly",
    title: "Talking things through can bring clarity. Ava helps you organize your thoughts.",
    description: "Sometimes the mind feels like a tangled mess. Speaking it out loud with Ava can help you sort through the noise, see patterns, and find your own answers—at your own pace.",
    image: SITE_MEDIA.usecase4,
    color: "#9333EA",
    gradient: "from-[#f8f0ff] to-[#f0e8ff]",
    iconBg: "#9333EA",
    number: "04",
  },
  {
    emoji: "💬",
    tag: "Emotional support",
    title: "Whether you're stressed, curious, or just need to talk, Ava is there for you.",
    description: "From celebrating small wins to carrying heavy feelings, Ava offers steady, caring companionship. No matter the mood, you're never navigating it alone.",
    image: SITE_MEDIA.usecase5,
    color: "#7C3AED",
    gradient: "from-[#f5f0ff] to-[#ede8ff]",
    iconBg: "#7C3AED",
    number: "05",
  },
];

function AnimatedCard({ data, index }: { data: typeof cases[0]; index: number }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLDivElement>(null);
  const isReversed = index % 2 !== 0;

  useEffect(() => {
    const card = cardRef.current;
    const text = textRef.current;
    const img = imgRef.current;
    if (!card || !text || !img) return;

    text.style.opacity = "0";
    text.style.transform = isReversed ? "translateX(50px)" : "translateX(-50px)";
    text.style.transition = "opacity 0.9s ease, transform 0.9s ease";

    img.style.opacity = "0";
    img.style.transform = isReversed ? "translateX(-50px)" : "translateX(50px)";
    img.style.transition = "opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            text.style.opacity = "1";
            text.style.transform = "none";
            img.style.opacity = "1";
            img.style.transform = "none";
            observer.unobserve(card);
          }
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(card);
    return () => observer.disconnect();
  }, [isReversed]);

  return (
    <div
      ref={cardRef}
      className={`flex flex-col ${isReversed ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-8 lg:gap-16 py-12 lg:py-16 border-b border-[#e8e0ff] last:border-0`}
    >
      {/* Image side */}
      <div ref={imgRef} className="w-full lg:w-1/2 rounded-3xl overflow-hidden relative" style={{ height: "340px" }}>
        <img
          src={data.image}
          alt={data.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-bold text-white flex items-center gap-1.5"
          style={{ background: data.color + "dd" }}
        >
          <span>{data.emoji}</span>
          <span>{data.tag}</span>
        </div>
      </div>

      {/* Text side */}
      <div ref={textRef} className="w-full lg:w-1/2 text-center lg:text-left">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold text-white mb-4"
          style={{ background: data.color }}
        >
          <span>{data.emoji}</span>
          <span>{data.tag}</span>
        </div>
        <h3 className="font-display text-xl md:text-2xl font-bold text-[#2d1b69] mb-4 leading-snug">
          {data.title}
        </h3>
        <p className="text-[#7a6aaa] text-base leading-relaxed mb-6">
          {data.description}
        </p>
        <button
          type="button"
          className="whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold cursor-pointer hover:opacity-90 transition-all hover:scale-105"
          style={{ background: `linear-gradient(135deg, ${data.color}, #A855F7)` }}
          onClick={() => openLiveDemoChat()}
        >
          <i className="ri-chat-smile-3-line w-4 h-4 flex items-center justify-center"></i>
          Talk to Ava now
        </button>
      </div>
    </div>
  );
}

export default function UseCases() {
  const headerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    el.style.opacity = "0";
    el.style.transform = "translateY(30px)";
    el.style.transition = "opacity 0.8s ease, transform 0.8s ease";

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.style.opacity = "1";
            el.style.transform = "none";
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="use-cases" className="py-14 md:py-20" style={{ background: "linear-gradient(180deg, #f5f0ff 0%, #ede8ff 60%, #f0eeff 100%)" }}>
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div ref={headerRef} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-[#f5f0ff] text-[#7C3AED] text-sm font-medium mb-3">
            <i className="ri-heart-3-line"></i>
            BENEFITS
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-[#2d1b69] mb-3">
            Why
            <br />
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #7C3AED, #A855F7)" }}
            >
              People are choosing Ava
            </span>
          </h2>
          <p className="text-[#7a6aaa] text-base max-w-xl mx-auto">
            Real moments. Real emotions. Ava is there for all of them.
          </p>
        </div>

        <div>
          {cases.map((c, i) => (
            <AnimatedCard key={i} data={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}